import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import classlist from 'app/utils/classlist';
import Skeleton from 'components/base/Skeleton';
import ErrorBox from 'components/ErrorBox';
import LangSwitch from 'components/LangSwitch';
import PageMain from 'components/layout/PageMain';
import WebWidget from 'components/WebWidget';
import Login from 'modules/Login';
import security from 'stores/security';
import UI from 'stores/ui';
import { OauthErrorCodeEnum } from 'types/error';
import type { HttpError } from 'utils/http';
import withLoadable from 'utils/withLoadable';
import ESOP from '../ESOP';
import Open from '../Open'; // 开户
import Page404 from '../Page404'; // 404页面
import Signup from '../Signup'; // 注册
import Environment from './Environment';
import Logo from './Logo';

import './style.scss';

// 异步加载的页面
const Modify = withLoadable(() => import('../Modify'));
const Proof = withLoadable(() => import('../Proof'));
const ComDemo = withLoadable(() => import('../ComDemo'));
const IBLink = withLoadable(() => import('../Iblink'));
const Upgrade = withLoadable(() => import('../Upgrade'));
const Capability = withLoadable(() => import('../Capability'));
const Inquiry = withLoadable(() => import('../Inquiry')); // 问卷相关
const Update = withLoadable(() => import('../Update')); // 更新资料
const Agreement = withLoadable(() => import('../Agreement')); // 支持单独签署的协议

const App: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<HttpError | null>(null);
    const { userInfo } = security;
    const getStatus = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            await security.ready();
        } catch (error) {
            setError(error);
        }

        setLoading(false);
    }, []);
    const renderRoutes = () => {
        if (loading) {
            return (
                <PageMain>
                    <Skeleton line={5} />
                </PageMain>
            );
        }

        if (error) {
            return (
                <PageMain>
                    <ErrorBox
                        error={error}
                        onClick={() => {
                            if (error.error_code === OauthErrorCodeEnum.MISS_MATCH_CUSTOMER_ID) {
                                const curPageHref = window.location.href;

                                window.location.href = `/logout?next=${curPageHref}`;
                            } else {
                                getStatus();
                            }
                        }}
                        btnContent={
                            error.error_code === OauthErrorCodeEnum.MISS_MATCH_CUSTOMER_ID
                                ? __('重新登录')
                                : __('Retry')
                        }
                    />
                </PageMain>
            );
        }

        return (
            <Routes>
                {/* 这里为了url可读性，单独为注册页面使用 /signup 地址 */}
                <Route path="login" element={<Login />} />
                <Route path="signup/*" element={<Signup />} />
                <Route path="open/*" element={<Open />} />
                <Route path="modify/*" element={<Modify />} />
                <Route path="link/*" element={<IBLink />} />
                <Route path="proof/:type" element={<Proof />} />
                <Route path="esop/*" element={<ESOP />} />
                <Route path="upgrade/*" element={<Upgrade />} />
                <Route path="update/*" element={<Update />} />
                <Route path="capability/*" element={<Capability />} />
                <Route path="inquiry/*" element={<Inquiry />} />
                <Route path="agreement/*" element={<Agreement />} />
                {process.env.NODE_ENV === 'development' && <Route path="components" element={<ComDemo />} />}
                <Route path="*" element={<Page404 />} />
            </Routes>
        );
    };

    useEffect(() => {
        // getStatus仅需要在mount时执行一次
        getStatus();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Logo />
            <div
                className={classlist('app-main', {
                    'no-logo': !UI.showLogo
                })}>
                {renderRoutes()}
            </div>
            {!loading && !UI.isTiger && userInfo && (
                <WebWidget
                    userHash={userInfo.uuidHash}
                    uuid={userInfo.uuid}
                    isMarscoAccount
                    isLogin={security.isLogin}
                />
            )}

            <footer className="app-footer">
                <LangSwitch />
                <Environment />
            </footer>
        </>
    );
};

export default App;
